/**
 * Anonymous Analytics Tracker
 * Logs login, session heartbeat, and feature usage events
 * No PII is collected — user_id is an opaque internal identifier
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics`;

function genSessionId() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

let _sessionId = null;
let _userId = null;
let _heartbeatTimer = null;
let _sessionStart = null;
let _pagesVisited = 0;

const AnalyticsTracker = {
  /** Call once on login success */
  trackLogin(userId) {
    _userId = userId || 'anonymous';
    _sessionId = genSessionId();
    _sessionStart = Date.now();
    _pagesVisited = 1;

    axios.post(`${API}/event/login`, {
      user_id: _userId,
      session_id: _sessionId,
    }).catch(() => {});

    // Start heartbeat every 5 minutes
    this._startHeartbeat();
  },

  /** Call when user navigates to a new view/page */
  trackPageView() {
    _pagesVisited += 1;
  },

  /** Call when user uses a specific feature */
  trackFeature(featureName) {
    if (!_sessionId) return;
    axios.post(`${API}/event/feature`, {
      user_id: _userId || 'anonymous',
      session_id: _sessionId,
      feature: featureName,
    }).catch(() => {});
  },

  /** Send a session heartbeat with duration */
  _sendHeartbeat() {
    if (!_sessionId || !_sessionStart) return;
    const durationSec = Math.round((Date.now() - _sessionStart) / 1000);
    axios.post(`${API}/event/session`, {
      user_id: _userId || 'anonymous',
      session_id: _sessionId,
      duration_seconds: durationSec,
      pages_visited: _pagesVisited,
    }).catch(() => {});
  },

  _startHeartbeat() {
    this._stopHeartbeat();
    _heartbeatTimer = setInterval(() => this._sendHeartbeat(), 5 * 60 * 1000);
  },

  _stopHeartbeat() {
    if (_heartbeatTimer) {
      clearInterval(_heartbeatTimer);
      _heartbeatTimer = null;
    }
  },

  /** Call on logout or page unload — sends final session event */
  endSession() {
    this._sendHeartbeat();
    this._stopHeartbeat();
    _sessionId = null;
    _userId = null;
    _sessionStart = null;
    _pagesVisited = 0;
  },
};

// Send final heartbeat on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    AnalyticsTracker._sendHeartbeat();
  });
}

export default AnalyticsTracker;
